import { CopyCheck, X } from 'lucide-react';
import type { PropertyFileResponse } from '@/shared/api';
import { formatBytes } from '@/shared/lib/format';

interface Props {
  files: PropertyFileResponse[];
  /** Einzelne Datei wieder abwählen — die einzige Auskunft, die die Sammel-Leiste nicht gibt. */
  onDeselect: (fileId: string) => void;
  onClear: () => void;
}

/**
 * Pane bei Mehrfach-Auswahl. Sie zeigt, **was** ausgewählt ist, und lässt einzelne Dateien
 * wieder heraus. Die Sammelaktionen bleiben in der Leiste über der Liste — sie hier zu
 * wiederholen würde nur zwei Wege zur selben Sache schaffen.
 */
export function AkteMultiSelectPane({ files, onDeselect, onClear }: Props) {
  const totalBytes = files.reduce((sum, f) => sum + f.sizeBytes, 0);
  const lockedCount = files.filter((f) => f.locked).length;

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-ink-faint text-xs font-medium uppercase tracking-wider">Auswahl</span>
        <button
          type="button"
          onClick={onClear}
          aria-label="Auswahl aufheben"
          className="text-ink-faint hover:text-ink -mr-2 flex h-11 w-11 items-center justify-center rounded"
        >
          <X size={18} />
        </button>
      </div>

      <div className="bg-card border-line mb-3 flex items-center gap-3 rounded-lg border px-4 py-3">
        <span className="bg-sealwax/10 text-sealwax flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
          <CopyCheck size={20} />
        </span>
        <div className="min-w-0">
          <p className="text-ink font-serif text-lg">{files.length} Dateien</p>
          <p className="text-ink-faint text-sm tabular-nums">
            {formatBytes(totalBytes)}
            {lockedCount > 0 && ` · ${lockedCount} gesperrt`}
          </p>
        </div>
      </div>

      <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
        {files.map((file) => (
          <li key={file.id} className="flex items-center gap-2">
            <span className="text-ink-soft min-w-0 flex-1 truncate text-sm" title={file.fileName}>
              {file.fileName}
            </span>
            <button
              type="button"
              onClick={() => onDeselect(file.id)}
              aria-label={`${file.fileName} abwählen`}
              className="text-ink-faint hover:text-sealwax flex h-11 w-11 shrink-0 items-center justify-center rounded"
            >
              <X size={14} />
            </button>
          </li>
        ))}
      </ul>

      <p className="text-ink-faint mt-3 shrink-0 pb-[env(safe-area-inset-bottom)] text-xs">
        Verschieben, Kategorie ändern, Herunterladen und Löschen stehen in der Leiste über der
        Liste.
      </p>
    </div>
  );
}
